import { useState, useMemo } from 'react';
import {
  IconButton,
  Paper,
  Toolbar,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router-dom';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import MapView from '../map/core/MapView';
import MapCamera from '../map/MapCamera';
import MapScale from '../map/MapScale';
import MapMarkers from '../map/MapMarkers';
import { formatTime } from '../common/util/formatter';
import ReportFilter from './components/ReportFilter';
import { useTranslation } from '../common/components/LocalizationProvider';
import { useCatch, useEffectAsync } from '../reactHelper';
import BackIcon from '../common/components/BackIcon';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { TOPBAR_HEIGHT } from '../common/components/TopBar';
import { GAP } from '../common/theme/layout';
// 2ECOMP: Mini-map components
import { MiniMapCell, MapExpandModal } from '../common/components/premium';
// Não usa PageLayout - mapa em tela cheia igual ReplayPage

const useStyles = makeStyles()((theme) => ({
  root: {
    height: '100%',
  },
  mapContainer: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1,
  },
  sidebar: {
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    zIndex: 3,
    left: 0,
    top: TOPBAR_HEIGHT + GAP.SMALL,
    margin: theme.spacing(1.5),
    width: theme.dimensions.drawerWidthDesktop,
    maxHeight: `calc(100vh - ${TOPBAR_HEIGHT + GAP.SMALL + 24}px)`,
    overflow: 'auto',
    [theme.breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
      top: TOPBAR_HEIGHT + GAP.SMALL,
    },
  },
  title: {
    flexGrow: 1,
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1.5),
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(1),
    },
    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(0.5),
    },
  },
}));

const CorrelationReportPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [devices, setDevices] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  // 2ECOMP: Expanded map state
  const [expandedMap, setExpandedMap] = useState(null);

  // Load devices on mount
  useEffectAsync(async () => {
    const response = await fetchOrThrow('/api/devices');
    const data = await response.json();
    setDevices(data.reduce((acc, d) => ({ ...acc, [d.id]: d }), {}));
  }, []);

  const handleSubmit = useCatch(async ({ deviceIds, from, to }) => {
    const deviceId = deviceIds?.[0];
    if (!deviceId) return;
    setLoading(true);
    try {
      const query = new URLSearchParams({ deviceId, from, to });
      const response = await fetchOrThrow(`/api/devices/near?${query.toString()}`);
      const data = await response.json();
      setItems(data);
      setSelectedItem(null);
    } finally {
      setLoading(false);
    }
  });

  // Create markers from correlation events
  // MapMarkers expects: { latitude, longitude, image, title }
  const markers = useMemo(() => {
    const result = [];
    items.forEach((item, index) => {
      // Reference device marker (blue/info)
      result.push({
        latitude: item.latitudeRef,
        longitude: item.longitudeRef,
        image: 'default-neutral',
        title: devices[item.deviceIdRef]?.name || `Ref ${index}`,
      });
      // Near device marker (red/error)
      result.push({
        latitude: item.latitudeNear,
        longitude: item.longitudeNear,
        image: 'default-error',
        title: devices[item.deviceIdNear]?.name || `Near ${index}`,
      });
    });
    return result;
  }, [items, devices]);

  // Camera position for selected item
  const cameraPosition = useMemo(() => {
    if (selectedItem) {
      return {
        latitude: selectedItem.latitudeRef,
        longitude: selectedItem.longitudeRef,
      };
    }
    if (items.length > 0) {
      return {
        latitude: items[0].latitudeRef,
        longitude: items[0].longitudeRef,
      };
    }
    return null;
  }, [selectedItem, items]);

  return (
    <div className={classes.root}>
        <div className={classes.mapContainer}>
          <MapView>
            <MapMarkers markers={markers} />
            {cameraPosition && (
              <MapCamera
                latitude={cameraPosition.latitude}
                longitude={cameraPosition.longitude}
              />
            )}
            <MapScale />
          </MapView>
        </div>
        <div
          className={classes.sidebar}
          style={{
            backgroundColor: tokens?.glassmorphism?.heavy?.background || 'rgba(13, 17, 23, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: tokens?.radius?.lg || '12px',
            border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
          }}
        >
          <Paper elevation={0} square sx={{ backgroundColor: 'transparent' }}>
            <Toolbar>
              <IconButton edge="start" sx={{ mr: 2 }} onClick={() => navigate(-1)}>
                <BackIcon />
              </IconButton>
              <Typography variant="h6" className={classes.title} noWrap>
                {t('correlationTitle')}
              </Typography>
            </Toolbar>
          </Paper>
          <Paper className={classes.content} square elevation={0} sx={{ backgroundColor: 'transparent', borderTop: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}` }}>
            <ReportFilter onShow={handleSubmit} deviceType="single" compact>
              <Box sx={{ width: '100%' }}>
                {/* Device selector is included in ReportFilter */}
              </Box>
            </ReportFilter>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : items.length > 0 ? (
              <TableContainer sx={{ maxHeight: 400, mt: 2 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('correlationDevice')}</TableCell>
                      <TableCell>{t('correlationTargetDevice')}</TableCell>
                      {/* 2ECOMP: Mini-map column */}
                      <TableCell sx={{ width: 100, textAlign: 'center' }}>{t('positionMap') || 'Mapa'}</TableCell>
                      <TableCell>{t('sharedDistance')}</TableCell>
                      <TableCell>{t('sharedTime')}</TableCell>
                      <TableCell>{t('sharedType')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((item, index) => (
                      <TableRow
                        key={item.id || index}
                        hover
                        selected={selectedItem?.id === item.id}
                        onClick={() => setSelectedItem(item)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell>
                          {devices[item.deviceIdRef]?.name || item.deviceIdRef}
                        </TableCell>
                        <TableCell>
                          {devices[item.deviceIdNear]?.name || item.deviceIdNear}
                        </TableCell>
                        {/* 2ECOMP: Mini-map with 2 markers */}
                        <TableCell sx={{ textAlign: 'center', py: 0.5 }}>
                          <MiniMapCell
                            latitude={item.latitudeRef}
                            longitude={item.longitudeRef}
                            secondaryLatitude={item.latitudeNear}
                            secondaryLongitude={item.longitudeNear}
                            size="xs"
                            markerIcon={item.eventType === 'proximityEnter' ? 'alert' : 'pin'}
                            onClick={() => setExpandedMap({
                              latitude: item.latitudeRef,
                              longitude: item.longitudeRef,
                              secondaryLatitude: item.latitudeNear,
                              secondaryLongitude: item.longitudeNear,
                              secondaryName: devices[item.deviceIdNear]?.name || item.deviceIdNear,
                              title: t('correlationTitle'),
                              subtitle: formatTime(item.serverTime, 'datetime'),
                              deviceName: devices[item.deviceIdRef]?.name,
                              eventType: item.eventType,
                            })}
                            tooltipTitle={t('mapExpand') || 'Expandir mapa'}
                          />
                        </TableCell>
                        <TableCell>
                          {item.distanceMt?.toFixed(2)} m
                        </TableCell>
                        <TableCell>
                          {formatTime(item.serverTime, 'datetime')}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.eventType === 'proximityEnter'
                              ? t('correlationAlertTypeApproach')
                              : t('correlationAlertTypeSeparation')}
                            size="small"
                            sx={{
                              backgroundColor: item.eventType === 'proximityEnter'
                                ? `${tokens.semantic?.error || '#f85149'}20`
                                : `${tokens.semantic?.success || '#00d4aa'}20`,
                              color: item.eventType === 'proximityEnter'
                                ? tokens.semantic?.error || '#f85149'
                                : tokens.semantic?.success || '#00d4aa',
                              borderColor: item.eventType === 'proximityEnter'
                                ? tokens.semantic?.error || '#f85149'
                                : tokens.semantic?.success || '#00d4aa',
                            }}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('sharedNoData')}
                </Typography>
              </Box>
            )}
          </Paper>
        </div>

      {/* 2ECOMP: Expanded map modal with 2 markers */}
      <MapExpandModal
        open={!!expandedMap}
        onClose={() => setExpandedMap(null)}
        latitude={expandedMap?.latitude}
        longitude={expandedMap?.longitude}
        secondaryLatitude={expandedMap?.secondaryLatitude}
        secondaryLongitude={expandedMap?.secondaryLongitude}
        secondaryName={expandedMap?.secondaryName}
        title={expandedMap?.title}
        subtitle={expandedMap?.subtitle}
        deviceName={expandedMap?.deviceName}
        eventType={expandedMap?.eventType}
        markerCategory={expandedMap?.eventType === 'proximityEnter' ? 'alarm' : 'default'}
      />
    </div>
  );
};

export default CorrelationReportPage;
